// src/pages/PublicEventsPage.js
// Public list of upcoming chamber events. Designed to be iframed into the Wix marketing site.
// No login required, no admin chrome.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const api = axios.create({ baseURL: '/api/public' });

export default function PublicEventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/events')
      .then(res => setEvents(res.data))
      .catch(err => {
        console.error('Failed to load events', err);
        setError('Unable to load events. Please try again later.');
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={pageStyle}>
      <div style={containerStyle}>
        <header style={headerStyle}>
          <h1 style={titleStyle}>Upcoming Events</h1>
          <p style={subtitleStyle}>New Jersey Jewish Business Alliance</p>
        </header>

        {loading && <div style={statusStyle}>Loading events…</div>}
        {error && <div style={{ ...statusStyle, color: '#b00' }}>{error}</div>}

        {!loading && !error && events.length === 0 && (
          <div style={statusStyle}>
            No upcoming events at the moment. Check back soon!
          </div>
        )}

        <div style={gridStyle}>
          {events.map(ev => (
            <EventCard key={ev.id} event={ev} />
          ))}
        </div>

        <footer style={footerStyle}>
          Questions? Email{' '}
          <a href="mailto:info@njjba.com" style={linkStyle}>info@njjba.com</a>
        </footer>
      </div>
    </div>
  );
}

// ─── EventCard ──────────────────────────────────────────────────────────────
function EventCard({ event }) {
  const dateLabel = formatDate(event.event_date);
  const timeLabel = formatTime(event.start_time, event.end_time);

  return (
    <Link to={`/embed/events/${event.id}`} style={cardLinkStyle}>
      <article style={cardStyle}>
        {event.has_flyer ? (
          <div style={cardFlyerStyle}>
            <img
              src={`/api/public/events/${event.id}/flyer`}
              alt={`Flyer for ${event.name}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        ) : (
          <div style={{ ...cardFlyerStyle, ...flyerPlaceholderStyle }}>
            <span style={{ fontSize: 48, opacity: 0.4 }}>📅</span>
          </div>
        )}
        <div style={cardBodyStyle}>
          <div style={dateBadgeStyle}>{dateLabel}</div>
          <h2 style={cardTitleStyle}>{event.name}</h2>
          {event.venue && <div style={venueStyle}>📍 {event.venue}</div>}
          {timeLabel && <div style={metaStyle}>🕐 {timeLabel}</div>}
          {event.description && (
            <p style={descStyle}>
              {truncate(event.description, 140)}
            </p>
          )}
          <div style={ctaStyle}>View details →</div>
        </div>
      </article>
    </Link>
  );
}

// ─── Styles (inline so we don't fight admin CSS) ────────────────────────────
const pageStyle = {
  minHeight:   '100vh',
  background:  '#f7f8fa',
  fontFamily:  '"Helvetica Neue", Arial, sans-serif',
  color:       '#1f2937',
  padding:     '32px 16px 48px',
};
const containerStyle = { maxWidth: 1100, margin: '0 auto' };
const headerStyle    = { textAlign: 'center', marginBottom: 32 };
const titleStyle     = { fontSize: 32, fontWeight: 700, margin: 0, color: '#1a3a5c' };
const subtitleStyle  = { fontSize: 15, color: '#6b7280', marginTop: 6 };
const statusStyle    = { textAlign: 'center', padding: 48, color: '#6b7280', fontSize: 16 };
const gridStyle      = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
  gap: 20,
};
const cardLinkStyle  = { textDecoration: 'none', color: 'inherit', display: 'block' };
const cardStyle      = {
  background:    '#fff',
  borderRadius:  10,
  overflow:      'hidden',
  boxShadow:     '0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)',
  transition:    'transform 0.15s ease, box-shadow 0.15s ease',
  cursor:        'pointer',
  height:        '100%',
  display:       'flex',
  flexDirection: 'column',
};
const cardFlyerStyle = {
  width: '100%',
  aspectRatio: '16 / 9',
  background: '#e5e7eb',
  overflow: 'hidden',
};
const flyerPlaceholderStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const cardBodyStyle  = { padding: 18, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 };
const dateBadgeStyle = {
  display:      'inline-block',
  background:   '#1a3a5c',
  color:        '#fff',
  padding:      '3px 10px',
  borderRadius: 4,
  fontSize:     12,
  fontWeight:   600,
  textTransform:'uppercase',
  letterSpacing: 0.5,
  alignSelf:    'flex-start',
};
const cardTitleStyle = { fontSize: 18, fontWeight: 600, margin: 0, color: '#1f2937', lineHeight: 1.3 };
const venueStyle     = { fontSize: 14, color: '#4b5563' };
const metaStyle      = { fontSize: 13, color: '#6b7280' };
const descStyle      = { fontSize: 14, color: '#4b5563', lineHeight: 1.5, margin: '4px 0 0' };
const ctaStyle       = { color: '#1a3a5c', fontWeight: 600, fontSize: 14, marginTop: 'auto', paddingTop: 8 };
const footerStyle    = { textAlign: 'center', marginTop: 40, color: '#6b7280', fontSize: 14 };
const linkStyle      = { color: '#1a3a5c', fontWeight: 600 };

// ─── Helpers ────────────────────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}
function formatTime(start, end) {
  if (!start) return '';
  const fmt = (t) => {
    const [hh, mm] = t.split(':');
    const h = parseInt(hh, 10);
    const m = mm || '00';
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m} ${period}`;
  };
  return end ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
}
function truncate(str, n) {
  if (!str) return '';
  return str.length <= n ? str : str.slice(0, n).trim() + '…';
}
